import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  buildingBasePath,
  dashboardPathFor,
  isBuildingOnlyPath,
} from "@/components/layout/buildingViewRoutes";
import { useStores } from "@/stores";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import type { ProjectStatus } from "@/domains/projects/status/projectStatus";
import { useIsProjectScopedMember } from "@/hooks/buildings/useIsProjectScopedMember";

export interface BuildingScopeProjectOption {
  id: number;
  name: string;
  status: ProjectStatus;
}

export interface UseBuildingScopeResult {
  /** Selected project id, or null for whole-building scope. */
  selectedProjectId: number | null;
  /** Scope the building to a specific project. */
  setProject: (projectId: number) => void;
  /** Project options for the picker (`{ id, name, status }`), building + org scoped. */
  projectOptions: BuildingScopeProjectOption[];
  /** True while the project options are loading and none are cached yet. */
  isLoading: boolean;
  /**
   * True once the options for this (org, building) have actually loaded.
   * Distinguishes "options still loading" from "this building has no
   * projects".
   */
  optionsLoaded: boolean;
  /**
   * Load error, if the last option load failed. With `optionsLoaded` false and
   * no load in flight this is a dead end — the load effect won't retry on its
   * own — so a consumer gating on `optionsLoaded` must offer `reloadOptions`.
   */
  optionsError: string | null;
  /** Retry a failed (or refresh a stale) option load. */
  reloadOptions: () => void;
}

const SCOPE_PARAM = "project";
/**
 * Folder navigation state owned by ProjectScopedFolderView. A folder belongs to
 * exactly one project, so every branch here that changes the effective project
 * has to drop it — otherwise the folder view loads a location outside the new
 * scope (empty list, breadcrumb resolving to nothing, uploads targeting a
 * foreign folder).
 */
const FOLDER_PARAM = "folderId";
/** Pre-`?project=` deep-link param, migrated by BuildingDocuments. */
const LEGACY_SCOPE_PARAM = "projectId";

/** Parse a `?project=` value into a positive integer id, or null. */
/** File selections the 3D model and Drawings viewers keep in the URL. */
const VIEWER_SELECTION_PARAMS = ["ids", "ifc", "compare", "focus"] as const;

function isViewerPath(pathname: string, buildingId: string): boolean {
  const base = buildingBasePath(buildingId);
  return pathname === `${base}/model` || pathname === `${base}/drawings`;
}

function parseProjectParam(raw: string | null): number | null {
  if (!raw) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Legacy `?projectId=` → `?project=` migration, as a pure transform so the
 * page-level effect that drives it stays two lines and the branches below are
 * unit-testable. Returns the params to write, or null when nothing should be.
 *
 * @param isRejectedLegacyId the caller has loaded the project options and this
 *   hook's reconcile effect has already rejected the legacy id. That effect
 *   strips the whole rejected scope (`project` + `projectId` + `folderId`) off
 *   the same render's snapshot the migration reads, so migrating the id here
 *   would write the rejected scope straight back.
 */
export function migrateLegacyScopeParams(
  searchParams: URLSearchParams,
  isRejectedLegacyId: boolean
): URLSearchParams | null {
  const legacyProjectId = searchParams.get(LEGACY_SCOPE_PARAM);
  if (!legacyProjectId || isRejectedLegacyId) return null;

  const next = new URLSearchParams(searchParams);
  next.delete(LEGACY_SCOPE_PARAM);

  // A legacy id that names no project rescues nothing by being promoted — and
  // `?project=abc` is junk the reconcile effect below can never clean up (its
  // strip is gated on a *parsed* `urlProjectId`, which is null for it). So the
  // unparseable id is simply dropped, together with the folder id it carried:
  // that folder lives in whatever project the broken id meant, which is not a
  // project anyone can name.
  const legacyId = parseProjectParam(legacyProjectId);
  if (legacyId == null) {
    next.delete(FOLDER_PARAM);
    return next;
  }

  // `?project=` outranks the legacy param everywhere else (this hook reads
  // `project ?? projectId`), so when both are on the URL the migration only
  // drops the legacy one — overwriting would revert a newer pick. The legacy
  // link's `folderId` names a folder in the *legacy* project, so it goes with
  // it: keeping it points the folder view — and its upload / new-subfolder
  // target — at a folder outside the project that won.
  //
  // Compared as parsed ids, the same way the hook resolves them. A `?project=`
  // that names no project (`abc`) outranks nothing — it has to be overwritten,
  // or the legacy id is lost and the reconcile effect's strip (gated on a
  // parsed id) never cleans the junk up either. And `07` vs `7` is the same
  // project, so the folder id survives.
  const scopedProjectId = parseProjectParam(next.get(SCOPE_PARAM));
  if (scopedProjectId == null) next.set(SCOPE_PARAM, legacyProjectId);
  else if (scopedProjectId !== legacyId) next.delete(FOLDER_PARAM);
  return next;
}

/**
 * Global building-scope hook — the single source of truth every building page
 * consumes to know whether it's scoped to the whole building or one project.
 *
 * Behavior depends on the sidebar view mode (`buildingViewModeStore`):
 *
 * **Building mode** — whole-building perspective. `selectedProjectId` is
 * always `null`; a stray `?project=` param is stripped from the URL **and the
 * remembered scope is forgotten**, so re-entering Projects mode always forces
 * a fresh pick from the dropdown. Exception: a *valid* `?project=` on any
 * route other than the building dashboard index is an explicit deep link — it
 * flips the view mode to Projects instead of being stripped.
 *
 * **Projects mode** — the picker scopes to one concrete project. A building
 * with exactly one project scopes to it without a pick. The URL
 * `?project=<id>` param is the source of truth per render; the
 * `buildingScopeStore` (in-memory only) carries the scope across the
 * Projects-view navigations whose links don't spell it out. The two are
 * reconciled one-way in an effect:
 *  - no URL param but a remembered + valid project → write it into the URL,
 *  - a URL param that isn't a valid (RLS/building-scoped) option → strip it and
 *    forget it (the scope falls back to unresolved, so the page gate asks for
 *    a pick),
 *  - a valid URL param that differs from memory → update memory to match
 *    (so shared/deep links update the remembered scope).
 *
 * Must be used inside an `observer` component — it reads MST observables.
 */
export function useBuildingScope(buildingId: string): UseBuildingScopeResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const root = useStores();
  const scopeStore = root.buildingScopeStore;
  const projectFolderStore = root.projectFolderStore;
  const viewModeStore = root.buildingViewModeStore;
  // A project-scoped member has no Building view (see useBuildingViewMode), so
  // their effective mode is always Projects — read here too, or this hook would
  // strip their `?project=` as Building mode does before the layout caught up.
  const projectsOnly = useIsProjectScopedMember();
  const viewMode = projectsOnly ? "projects" : viewModeStore.getViewMode(buildingId);
  const { currentOrganization, isReady } = useOrganizationContext();

  const orgId = currentOrganization?.id;
  const buildingIdNum = Number(buildingId);
  const hasBuilding = !!buildingId && Number.isFinite(buildingIdNum) && buildingIdNum > 0;

  const reloadOptions = useCallback(() => {
    if (!orgId || !hasBuilding || projectFolderStore.isLoadingProjects) return;
    void projectFolderStore.loadProjectOptions(orgId, buildingIdNum).catch(() => {
      // Surfaced via projectFolderStore.projectsError (see `optionsError`); the
      // selector degrades to an empty list rather than throwing.
    });
  }, [orgId, hasBuilding, buildingIdNum, projectFolderStore]);

  // Load project options once per (org, building); the store caches them. A
  // failed load caches nothing and this effect's deps don't change afterwards,
  // so it never retries by itself — that's what `reloadOptions` is for.
  useEffect(() => {
    if (!isReady || !orgId || !hasBuilding) return;
    if (projectFolderStore.hasProjectOptions(orgId, buildingIdNum)) return;
    reloadOptions();
  }, [isReady, orgId, hasBuilding, buildingIdNum, projectFolderStore, reloadOptions]);

  const optionsLoaded =
    !!orgId && hasBuilding && projectFolderStore.hasProjectOptions(orgId, buildingIdNum);

  // Read the options live (the consuming component is an `observer`, so this
  // read subscribes it to option loads) and map to plain objects each render.
  // The list is small and the selector re-renders on store changes anyway, so a
  // fresh array per render is fine — and it avoids a stale memo whose deps
  // (org/building/store refs) never change when the options load ASYNC, which
  // previously left validation empty and stripped a freshly-picked project on
  // first visit.
  const projectOptions: BuildingScopeProjectOption[] =
    orgId && hasBuilding
      ? projectFolderStore.getProjectOptions(orgId, buildingIdNum).map((p) => ({
          id: p.id,
          name: p.name,
          status: p.status,
        }))
      : [];
  // Stable content signature for the option ids — used to memoize `isValidOption`
  // so its identity only changes when the actual options change (keeps the
  // reconcile effect from re-running every render) while still reacting to loads.
  const optionsSignature = projectOptions.map((p) => p.id).join(",");

  // The legacy param is a scope authority until BuildingDocuments swaps it for
  // `?project=`: that migration is a parent effect, and the folder view mounts
  // (and its own effects run) in the same commit, one flush earlier. Resolving
  // to the *remembered* project in that window would load the URL's `folderId`
  // — which belongs to the legacy id — against the wrong project.
  const urlProjectId = parseProjectParam(
    searchParams.get(SCOPE_PARAM) ?? searchParams.get(LEGACY_SCOPE_PARAM)
  );
  const rememberedProjectId = hasBuilding ? scopeStore.getProjectId(buildingId) : null;

  const isValidOption = useCallback(
    (id: number) => optionsSignature.length > 0 && optionsSignature.split(",").includes(String(id)),
    [optionsSignature]
  );

  // With exactly one project visible here there is nothing to choose, for any
  // role: Projects view scopes to it without asking. (A project-scoped member
  // sees only their assigned projects, so for them this is their one project.)
  const soleProjectId = optionsLoaded && projectOptions.length === 1 ? projectOptions[0].id : null;

  // Resolved scope for this render. Building mode is always whole-building.
  // In Projects mode the URL wins, falling back to memory; an id the options
  // have loaded and rejected resolves to null (All projects).
  const selectedProjectId = useMemo<number | null>(() => {
    if (viewMode === "building") return null;
    const candidate = urlProjectId ?? rememberedProjectId ?? soleProjectId;
    if (candidate == null) return null;
    if (optionsLoaded && !isValidOption(candidate)) return soleProjectId;
    return candidate;
  }, [viewMode, urlProjectId, rememberedProjectId, soleProjectId, optionsLoaded, isValidOption]);

  // Every scope-bearing param this hook owns, dropped in one navigation. A
  // `folderId` is a location *inside* a project scope, so it goes wherever the
  // scope does — including when the URL carried no `?project=` at all (a legacy
  // link, or a hand-edited one).
  const stripScopeParams = useCallback(() => {
    const stale = [SCOPE_PARAM, LEGACY_SCOPE_PARAM, FOLDER_PARAM].filter((p) =>
      searchParams.has(p)
    );
    if (stale.length === 0) return;
    const next = new URLSearchParams(searchParams);
    for (const p of stale) next.delete(p);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // A `?project=` param outside the building dashboard index is an explicit
  // deep link (a shared `…/documents?project=7`, or a projects-only route
  // opened cold). Adopt Projects mode instead of stripping it: this hook runs
  // in the child page too, whose effects fire *before* BuildingLayout's
  // route→mode sync, so stripping here would destroy the param before the mode
  // ever flipped. Gated on the resolved id, not on `?project=`: a legacy
  // `?projectId=` is scope authority too (only the Files page migrates it), so
  // gating on the canonical param alone left such a link stuck in Building mode
  // with its params never cleaned up.
  //
  // Answers whether Building mode should leave the URL alone — waiting for the
  // options counts, so an unknown / RLS-denied id isn't stripped before it has
  // been judged. Once they are loaded and reject it, it falls through to the
  // strip.
  const adoptProjectsFromDeepLink = useCallback((): boolean => {
    if (urlProjectId == null || isBuildingOnlyPath(pathname, buildingId)) return false;
    if (!optionsLoaded || projectFolderStore.isLoadingProjects) return true;
    if (!isValidOption(urlProjectId)) return false;
    viewModeStore.setViewMode(buildingId, "projects");
    return true;
  }, [
    urlProjectId,
    pathname,
    buildingId,
    optionsLoaded,
    projectFolderStore.isLoadingProjects,
    isValidOption,
    viewModeStore,
  ]);

  // Building mode: the whole-building perspective never carries a project
  // param — strip a stray one and forget the remembered scope.
  const reconcileBuildingMode = useCallback(() => {
    if (adoptProjectsFromDeepLink()) return;

    // Being in Building view forgets the project scope, so switching back to
    // Projects view always forces a fresh pick from the dropdown instead of
    // silently restoring whatever was last selected.
    if (rememberedProjectId != null) scopeStore.clearScope(buildingId);

    stripScopeParams();
  }, [adoptProjectsFromDeepLink, rememberedProjectId, scopeStore, buildingId, stripScopeParams]);

  // No URL param but a valid remembered project → restore it into the URL. A
  // member's sole project stands in when nothing valid is remembered.
  const restoreRememberedScope = useCallback(() => {
    const restoreId =
      rememberedProjectId != null && isValidOption(rememberedProjectId)
        ? rememberedProjectId
        : soleProjectId;
    if (restoreId == null) return;
    const next = new URLSearchParams(searchParams);
    next.set(SCOPE_PARAM, String(restoreId));
    // The restored project is not the one this URL was written for, so any
    // folder id still on it belongs to the previous scope. A legacy
    // `?projectId=` link never lands here — it resolves as `urlProjectId`
    // above — unless its id is unparseable, and then the folder doesn't
    // belong to the restored project either.
    next.delete(FOLDER_PARAM);
    setSearchParams(next, { replace: true });
  }, [rememberedProjectId, isValidOption, soleProjectId, searchParams, setSearchParams]);

  // Projects mode: only reconcile once options are loaded AND not
  // mid-refresh, so we never strip a param we simply haven't validated yet.
  const reconcileProjectsMode = useCallback(() => {
    if (!optionsLoaded || projectFolderStore.isLoadingProjects) return;

    if (urlProjectId == null) {
      restoreRememberedScope();
      return;
    }

    // Invalid URL param → strip it and forget the scope. Both params that feed
    // `urlProjectId` have to go: leaving a rejected legacy id behind keeps
    // resolving to it forever (the restore branch could never run) on every
    // route without BuildingDocuments' migration effect to clean it up.
    if (!isValidOption(urlProjectId)) {
      stripScopeParams();
      scopeStore.clearScope(buildingId);
      return;
    }

    // Valid URL param differing from memory → sync memory to the URL.
    if (urlProjectId !== rememberedProjectId) scopeStore.setScope(buildingId, urlProjectId);
  }, [
    optionsLoaded,
    projectFolderStore.isLoadingProjects,
    urlProjectId,
    rememberedProjectId,
    isValidOption,
    restoreRememberedScope,
    stripScopeParams,
    scopeStore,
    buildingId,
  ]);

  // One-way reconcile between the URL and remembered scope, per view mode. The
  // two modes reconcile nothing in common, so they are two callbacks rather
  // than one branch — the combined body was over the complexity threshold.
  useEffect(() => {
    if (!hasBuilding) return;
    if (viewMode === "building") {
      reconcileBuildingMode();
      return;
    }
    reconcileProjectsMode();
  }, [hasBuilding, viewMode, reconcileBuildingMode, reconcileProjectsMode]);

  // `setProject` is also fired by the shared create-project dialog, which
  // captured the callback when it opened — one or more renders before the
  // create resolves. Reading the location through a ref keeps that write
  // against the URL as it is when it lands, not the snapshot the picker closed
  // over. The pathname matters as much as the params: a captured
  // `setSearchParams` navigates to `"?" + params`, which react-router resolves
  // against the pathname it was created on, so a dialog resolving after the
  // user has navigated away would yank them back to the page they left.
  //
  // Synced in an effect rather than during render, so a render that is thrown
  // away can never move it. The ceiling that buys is one pick per commit: two
  // `setProject` calls dispatched from one handler would both read the
  // pre-navigation URL and the second would win. Every call site fires exactly
  // one, off a user's pick, and last-pick-wins is what a double pick means.
  const locationRef = useRef({ searchParams, pathname });
  useEffect(() => {
    locationRef.current = { searchParams, pathname };
  }, [searchParams, pathname]);

  const setProject = useCallback(
    (projectId: number) => {
      if (!hasBuilding) return;
      // A project scope only survives in Projects view — Building view strips
      // `?project=` and forgets the scope on its next reconcile. The pick sets
      // the mode it needs rather than trusting the mode it was started in: the
      // create dialog can resolve long after the user flipped views.
      viewModeStore.setViewMode(buildingId, "projects");
      const { searchParams: current, pathname: currentPathname } = locationRef.current;
      const next = new URLSearchParams(current);
      next.set(SCOPE_PARAM, String(projectId));
      // A legacy id left on the URL is a second scope authority: `?project=`
      // outranks it here, but BuildingDocuments' migration effect rewrites
      // `?project=` *from* it, silently reverting this pick on the Files page.
      next.delete(LEGACY_SCOPE_PARAM);
      // Switching scope starts at the new project's root; keeping the previous
      // folder id would poison Back/Forward history with a foreign location.
      next.delete(FOLDER_PARAM);
      // A viewer's applied files belong to the project being left: kept, the
      // canvas goes on rendering them while the picker lists only the new
      // project's files. A re-pick of the same project keeps them.
      const leavingProjectId =
        parseProjectParam(current.get(SCOPE_PARAM) ?? current.get(LEGACY_SCOPE_PARAM)) ??
        scopeStore.getProjectId(buildingId);
      if (isViewerPath(currentPathname, buildingId) && leavingProjectId !== projectId) {
        for (const param of VIEWER_SELECTION_PARAMS) next.delete(param);
      }
      // A building-only route has no Projects-view rendering, so BuildingLayout
      // syncs the mode straight back ("route wins, mode follows") and the scope
      // is stripped again. Land on the projects dashboard instead — the same
      // follow-through the sidebar's picker performs, and the only path this
      // hook can be on when a create resolves after a flip to Building view.
      if (isBuildingOnlyPath(currentPathname, buildingId)) {
        // Only the scope crosses. This is the one branch that changes PAGE,
        // and every other param this app reads belongs to the page that wrote
        // it (`view=trash` on Files, `focus=` in the viewer, `ids=` on
        // Drawings) — so forwarding the building dashboard's query string to
        // the projects dashboard can carry nothing the target understands, and
        // only risks handing it another page's state under a name they happen
        // to share. The same-route branch below still keeps them, where they
        // do belong.
        //
        // The URL is the only carrier here, deliberately: a remembered scope
        // the URL does not name yet is what the reconcile effect above
        // restores, and it would restore it onto the route being left —
        // replacing this navigation. It syncs memory from the param on
        // arrival instead.
        navigate(`${dashboardPathFor("projects", buildingId)}?${SCOPE_PARAM}=${projectId}`);
        return;
      }
      scopeStore.setScope(buildingId, projectId);
      // Re-picking the project already in scope is a real click — the pickers
      // open on the current project and confirming it is a normal thing to do.
      // It is only a no-op when the params come out identical: inside a project
      // folder it still has to drop `folderId`, which is how re-picking
      // navigates back to the project root. Compared here rather than in the
      // pickers, which see the id alone and would skip that cleanup; a URL that
      // changes nothing leaves a Back press that looks dead.
      if (next.toString() === current.toString()) return;
      // Navigated by absolute path, never `setSearchParams` — see the ref above.
      navigate(`${currentPathname}?${next}`);
    },
    [hasBuilding, scopeStore, buildingId, viewModeStore, navigate]
  );

  return {
    selectedProjectId,
    setProject,
    projectOptions,
    isLoading: projectFolderStore.isLoadingProjects && projectOptions.length === 0,
    optionsLoaded,
    optionsError: optionsLoaded ? null : projectFolderStore.projectsError,
    reloadOptions,
  };
}
